// Core modules
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { NgModule, APP_INITIALIZER, ErrorHandler, Provider } from '@angular/core';
import { Router } from '@angular/router';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// External modules
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

// App modules
import { HomeModule } from 'src/app/home/home.module';
import { CoreModule } from '@app-core/core.module';
import { SharedModule } from '@app-shared/shared.module';
import { AppRoutingModule } from './app-routing.module';

// components
import { AppComponent } from './app.component';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ServiceWorkerModule } from '@angular/service-worker';

import { authModules } from '@config/modules';
import { environment } from '@env/environment';
import { metaReducers } from './reducers';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import * as Sentry from '@sentry/angular';
import { HttpInterceptorService } from '@app-core/interceptors/httpInterceptor.service';
import { StartupService } from '@app-core/services/startup/startup.service';
import { StorageService } from '@app-core/services/storage/storage.service';
import { AccessService } from '@app-core/services/access/access.service';
import { initializeApiServices } from '@app-core/constants/api.constants';
import { CLIENT_CONFIG } from '@config/config';
import { setupGeotabRouter } from './geotab/geotab-router/geotab-router';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

function startUpServiceFactory(startupService: StartupService) {
  return () => startupService.load();
}

const SentryProviders: Provider[] = (() => {
  if (Boolean(CLIENT_CONFIG.disableSentry)) {
    if (Boolean(environment.isGeotabEnv))
      return [
        {
          provide: ErrorHandler,
          useClass: class extends ErrorHandler {
            override handleError(_error: any): void {}
          },
        },
      ];
    return [];
  }
  return [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
        logErrors: !Boolean(environment.isGeotabEnv),
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
  ];
})();

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    LeafletModule.forRoot(),
    StoreModule.forRoot({}, { metaReducers }),
    MatProgressSpinnerModule,
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    CoreModule,
    SharedModule,
    ...authModules,
    HomeModule,
    AppRoutingModule,
    !environment.isGeotabEnv ? ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }) : [],
  ],
  providers: [
    Boolean(environment.isGeotabEnv)
      ? {
          provide: Router,
          useFactory: setupGeotabRouter,
        }
      : [],
    {
      provide: APP_INITIALIZER,
      useFactory: (storage: StorageService, access: AccessService) => {
        return () => {
          initializeApiServices(storage, access);
          return Promise.resolve();
        };
      },
      deps: [StorageService, AccessService],
      multi: true,
    },
    SentryProviders,
    {
      provide: APP_INITIALIZER,
      useFactory: startUpServiceFactory,
      deps: [StartupService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorService,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
